import React, { useEffect, useRef, useState } from 'react';
import styles from './index.module.scss';
import { Tooltip } from 'antd';
import LanguageDropdown from '@/components/LanguageDropdown';
import DropdownMenu, { DropdownMenuItem } from './DropdownMenu';
import type { CustomerServiceHoursDisplay } from '@/util/customerServiceHours';

interface TopMetaBarProps {
  isHomePage?: boolean;
  isWidePage?: boolean;
  siteName: string;
  languageName: string;
  languageMap?: Record<string, string>;
  nationLogoMap: Record<string, string>;
  currentLanguage: string;
  userTypeName?: string;
  nickName?: string;
  isGuestUser: boolean;
  showCustomerService: boolean;
  showLogout: boolean;
  onCustomerServiceClick: () => void;
  onChangeLanguage: (lang: string) => void;
  onWorkspaceClick: () => void;
  onBatchCartClick: () => void;
  onLogoutClick: () => void;
  workspaceText: string;
  batchAddCartText: string;
  logoutText: string;
  customerServiceText: string;
  customerServiceHours?: CustomerServiceHoursDisplay | null;
  selectedAddressText?: string;
  onAddressClick?: () => void;
}

const TopMetaBar: React.FC<TopMetaBarProps> = ({
  isHomePage = false,
  isWidePage = false,
  siteName,
  languageName,
  languageMap = {},
  nationLogoMap,
  currentLanguage,
  userTypeName,
  nickName,
  isGuestUser,
  showCustomerService,
  showLogout,
  onCustomerServiceClick,
  onChangeLanguage,
  onWorkspaceClick,
  onBatchCartClick,
  onLogoutClick,
  workspaceText,
  batchAddCartText,
  logoutText,
  customerServiceText,
  customerServiceHours,
  selectedAddressText,
  onAddressClick,
}) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuTimerRef = useRef<NodeJS.Timeout | null>(null);
  const userMenuItems: DropdownMenuItem[] = [
    {
      key: 'workspace',
      iconClassName: 'icon-workbench',
      label: workspaceText,
      onClick: onWorkspaceClick,
    },
    {
      key: 'batchCart',
      iconClassName: 'icon-batch',
      label: batchAddCartText,
      onClick: onBatchCartClick,
    },
    ...(showLogout
      ? [
          {
            key: 'logout',
            iconClassName: 'icon-quit',
            label: logoutText,
            onClick: onLogoutClick,
          },
        ]
      : []),
  ];

  useEffect(() => {
    return () => {
      if (userMenuTimerRef.current) {
        clearTimeout(userMenuTimerRef.current);
      }
    };
  }, []);

  const openUserMenu = () => {
    if (userMenuTimerRef.current) {
      clearTimeout(userMenuTimerRef.current);
      userMenuTimerRef.current = null;
    }
    setShowUserMenu(true);
  };

  const closeUserMenu = () => {
    if (userMenuTimerRef.current) {
      clearTimeout(userMenuTimerRef.current);
    }
    userMenuTimerRef.current = setTimeout(() => {
      setShowUserMenu(false);
    }, 120);
  };
  const customerServiceHoursTooltip = customerServiceHours ? (
    <span className={styles.customerServiceHoursContent}>
      <span className={styles.customerServiceHoursTitle}>{customerServiceHours.hoursTitle}</span>
      <span className={styles.customerServiceHoursLine}>{customerServiceHours.weekdayText}</span>
      {customerServiceHours.timeRanges.map(timeRange => (
        <span className={styles.customerServiceHoursLine} key={timeRange}>
          {timeRange}
        </span>
      ))}
    </span>
  ) : null;

  return (
    <div
      className={`${styles.topMetaBar} ${isHomePage ? styles.topMetaBarHome : ''} ${
        isWidePage ? styles.topMetaBarWide : ''
      }`}
    >
      <div
        className={`${styles.topMetaContent} ${isHomePage ? styles.topMetaContentHome : ''} ${
          isWidePage ? styles.topMetaContentWide : ''
        }`}
      >
        <div className={styles.topMetaLeft}>
          <div className={styles.topMetaAddress} onClick={onAddressClick}>
            <span className={`iconFont icon-adress ${styles.topMetaIcon}`}></span>
            <span>{!isGuestUser && selectedAddressText !== undefined ? selectedAddressText : siteName}</span>
            <span className={`iconFont icon-expand ${styles.topMetaArrow}`}></span>
          </div>
        </div>
        <div className={styles.topMetaRight}>
          {!isGuestUser && userTypeName && <span>{userTypeName}</span>}
          {!isGuestUser && nickName && (
            <>
              <span className={styles.topMetaDivider}>|</span>
              <div
                className={`${styles.menuTrigger} ${showUserMenu ? styles.menuTriggerOpen : ''}`}
                onMouseEnter={openUserMenu}
                onMouseLeave={closeUserMenu}
              >
                <span>{nickName}</span>
                <span className={`iconFont icon-expand ${styles.topMetaArrow}`}></span>
                <DropdownMenu
                  visible={showUserMenu}
                  items={userMenuItems}
                  menuClassName={styles.dropdownMenu}
                  iconClassName={styles.dropdownMenuIcon}
                  onMouseEnter={openUserMenu}
                  onMouseLeave={closeUserMenu}
                />
              </div>
            </>
          )}
          {showCustomerService && (
            <>
              <span className={styles.topMetaDivider}>|</span>
              <span className={styles.customerServiceTrigger}>
                <Tooltip
                  title={customerServiceHoursTooltip}
                  placement="bottom"
                  overlayClassName={styles.customerServiceHoursTooltip}
                  arrow={{ pointAtCenter: true }}
                  autoAdjustOverflow
                  destroyTooltipOnHide
                >
                  <button type="button" className={styles.topMetaLink} onClick={onCustomerServiceClick}>
                    {customerServiceText}
                  </button>
                </Tooltip>
              </span>
            </>
          )}
          <span className={styles.topMetaDivider}>|</span>
          <LanguageDropdown
            currentLanguage={currentLanguage}
            languageMap={{ ...languageMap, [currentLanguage]: languageName }}
            nationLogoMap={nationLogoMap}
            onChangeLanguage={onChangeLanguage}
            theme="light"
            className={styles.languageDropdown}
            hideCurrentInMenu
          />
        </div>
      </div>
    </div>
  );
};

export default TopMetaBar;
